import { Component } from '@theme/component';

/** Safety net for the closing transition, in milliseconds. */
const COLLAPSE_TIMEOUT = 700;

/**
 * FAQ accordion.
 *
 * The rows are native `<details>` / `<summary>`, so the section works with JavaScript disabled —
 * including exclusivity, which the browser handles through the shared `name` attribute. Once this
 * component upgrades it takes the toggling over so the panel height can be animated
 * (`grid-template-rows: 0fr → 1fr`), which the native element cannot do on its own.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [list]
 * @property {HTMLDetailsElement[]} [items]
 *
 * @extends {Component<Refs>}
 */
export class OcFaqComponent extends Component {
  /** @type {AbortController | null} */
  #abort = null;

  /** Pending collapse per item: the timer and the `transitionend` listener that end it. */
  #closing = new WeakMap();

  #reduced = false;

  connectedCallback() {
    super.connectedCallback();

    this.#reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

    this.#sync();
    this.#listenToEditor();
  }

  /** Re-runs after a Section Rendering API re-render + morph. */
  updatedCallback() {
    super.updatedCallback();

    this.#sync();
  }

  disconnectedCallback() {
    for (const item of this.items) this.#clearPending(item);

    this.#abort?.abort();
    this.#abort = null;

    super.disconnectedCallback();
  }

  /** Whether more than one answer may stay open. */
  get allowMultiple() {
    return this.getAttribute('data-allow-multiple') === 'true';
  }

  /** The question rows, in document order. */
  get items() {
    return /** @type {HTMLDetailsElement[]} */ (Array.from(this.querySelectorAll('[data-oc-faq-item]')));
  }

  /**
   * Without JS the exclusive accordion is handled natively by `name`. Now that we drive it — with
   * animation — the attribute has to go, or the browser snaps siblings shut mid-transition.
   */
  #sync() {
    for (const item of this.items) {
      if (!this.allowMultiple) item.removeAttribute('name');

      if (item.open) {
        item.setAttribute('data-oc-open', '');
      } else {
        item.removeAttribute('data-oc-open');
      }
    }
  }

  /* -------------------------------------------------------------------------- */
  /* Interaction                                                                */
  /* -------------------------------------------------------------------------- */

  /**
   * Toggles the question a `<summary>` belongs to. Bound from `on:click` on the summary.
   *
   * @param {Event} event
   */
  toggleQuestion(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const item = /** @type {HTMLDetailsElement | null} */ (event.target.closest('[data-oc-faq-item]'));
    if (!item || !this.contains(item)) return;

    event.preventDefault();

    if (item.hasAttribute('data-oc-open')) {
      this.collapse(item);
    } else {
      this.expand(item);
    }
  }

  /**
   * Opens a question, closing the others when exclusivity is on.
   *
   * @param {HTMLDetailsElement} item
   */
  expand(item) {
    if (!this.allowMultiple) {
      for (const other of this.items) {
        if (other !== item) this.collapse(other);
      }
    }

    this.#clearPending(item);
    item.open = true;

    if (this.#reduced) {
      item.setAttribute('data-oc-open', '');
      return;
    }

    // Two frames: the first commits `0fr` with the panel laid out, the second starts the transition.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (item.open) item.setAttribute('data-oc-open', '');
      });
    });
  }

  /**
   * Closes a question, keeping it `[open]` until the height transition has finished.
   *
   * @param {HTMLDetailsElement} item
   */
  collapse(item) {
    if (!item.open) return;

    this.#clearPending(item);
    item.removeAttribute('data-oc-open');

    if (this.#reduced) {
      item.open = false;
      return;
    }

    const panel = item.querySelector('[data-oc-faq-panel]');

    if (!panel) {
      item.open = false;
      return;
    }

    const controller = new AbortController();

    const done = (/** @type {Event} */ event) => {
      if (event && event.target !== panel) return;

      this.#clearPending(item);

      if (!item.hasAttribute('data-oc-open')) item.open = false;
    };

    panel.addEventListener('transitionend', done, { signal: controller.signal });

    this.#closing.set(item, {
      controller,
      timer: setTimeout(() => done(new Event('transitionend')), COLLAPSE_TIMEOUT),
    });
  }

  /**
   * @param {HTMLDetailsElement} item
   */
  #clearPending(item) {
    const pending = this.#closing.get(item);

    if (!pending) return;

    clearTimeout(pending.timer);
    pending.controller.abort();
    this.#closing.delete(item);
  }

  /* -------------------------------------------------------------------------- */
  /* Public API — the shape `assets/theme-editor.js` calls on a slideshow.       */
  /* -------------------------------------------------------------------------- */

  /** No autoplay to stop; kept for signature parity. */
  pause() {}

  /** No autoplay to restart; kept for signature parity. */
  resume() {}

  /**
   * Opens the question at `index` (HORIZON-SPEC 10.2).
   *
   * @param {number|string} index - The index of the question to open.
   * @param {Event} [event] - The event that triggered the selection.
   * @param {object} [options] - The options for the selection.
   * @param {boolean} [options.animate=true] - Unused; kept for signature parity.
   */
  select(index, event, options = {}) {
    const item = this.items[Number(index)];

    if (item) this.expand(item);
  }

  /**
   * `assets/theme-editor.js` only knows about `slideshow-component`, and it is overwritten on
   * every theme update — so this component carries its own listener (HORIZON-SPEC 10.2).
   */
  #listenToEditor() {
    if (!window.Shopify?.designMode) return;

    this.#abort?.abort();
    this.#abort = new AbortController();

    const { signal } = this.#abort;

    document.addEventListener(
      'shopify:block:select',
      (event) => {
        if (!(event.target instanceof HTMLElement)) return;

        const item = /** @type {HTMLDetailsElement | null} */ (event.target.closest('[data-oc-faq-item]'));
        if (!item || item.closest('oc-faq-component') !== this) return;

        this.expand(item);
      },
      { signal }
    );

    document.addEventListener(
      'shopify:block:deselect',
      (event) => {
        if (!(event.target instanceof HTMLElement)) return;

        const item = /** @type {HTMLDetailsElement | null} */ (event.target.closest('[data-oc-faq-item]'));
        if (!item || item.closest('oc-faq-component') !== this) return;
        if (this.allowMultiple) return;

        this.collapse(item);
      },
      { signal }
    );
  }
}

if (!customElements.get('oc-faq-component')) {
  customElements.define('oc-faq-component', OcFaqComponent);
}
